/**
 * Profile service
 * Handles user profile data, stats, and activity
 */

import axios from "axios";
import api from "../api";
import { isUserVerified } from "./verification";
import { API_BASE_URL } from "../constants";

export interface ProfileStats {
  posts_count: number;
  docs_count: number;
  comments_count: number;
  reactions_count: number;
  reports_count: number;
}

export interface UserProfile {
  id: string | null;
  username: string;
  email: string;
  full_name: string;
  bio: string;
  location: string;
  created_at: string;
  profilePicture: string;
  is_email_verified: boolean;
  onboarding_completed: boolean;
}

export interface ActivityRecord {
  id: string;
  action: string;
  doc: {
    id: string;
    title: string;
    slug: string;
  };
  comment?: {
    id: string;
    comment_text: string;
    doc_title: string;
    doc_slug: string;
  } | null;
  timestamp: string;
}

const ACTIVITY_ENDPOINT = "/api/docs/activity/";

const DEFAULT_PROFILE_STATS: ProfileStats = {
  posts_count: 0,
  docs_count: 0,
  comments_count: 0,
  reactions_count: 0,
  reports_count: 0,
};

function extractAxiosMessage(error: unknown, fallbackMessage: string): string {
  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as { detail?: string; error?: string; message?: string } | undefined;
    return payload?.detail || payload?.error || payload?.message || error.message || fallbackMessage;
  }

  if (error instanceof Error) {
    return error.message || fallbackMessage;
  }

  return fallbackMessage;
}

function coerceString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  return fallback;
}

function coerceOptionalString(value: unknown): string | null {
  const normalized = coerceString(value, "").trim();
  return normalized.length ? normalized : null;
}

function mapActivityRecord(raw: any): ActivityRecord {
  const docRaw = raw?.doc ?? {};
  const timestamp = coerceString(raw?.timestamp, new Date().toISOString());
  const docId = docRaw?.id ? String(docRaw.id) : "";

  const doc = {
    id: docId,
    title: coerceString(docRaw?.title, "Untitled doc"),
    slug: coerceString(docRaw?.slug, ""),
  };

  const commentRaw = raw?.comment ?? raw?.meta?.comment ?? null;

  const comment = commentRaw
    ? {
        id: commentRaw?.id ? String(commentRaw.id) : "",
        comment_text: coerceOptionalString(commentRaw?.comment_text ?? commentRaw?.body_md ?? "") ?? "",
        doc_title: coerceOptionalString(commentRaw?.doc_title ?? doc.title) ?? doc.title,
        doc_slug: coerceOptionalString(commentRaw?.doc_slug ?? doc.slug) ?? doc.slug,
      }
    : null;

  return {
    id: coerceString(
      raw?.id,
      `activity-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
    ),
    action: coerceString(raw?.action, "Updated Doc"),
    doc,
    comment,
    timestamp,
  };
}

function extractActivityItems(payload: any): any[] {
  if (Array.isArray(payload?.results)) {
    return payload.results;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
}

async function fetchActivityFeed(params?: Record<string, unknown>): Promise<ActivityRecord[]> {
  try {
    const response = await api.get(ACTIVITY_ENDPOINT, { params });
    const items = extractActivityItems(response.data);
    return items.map(mapActivityRecord);
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return [];
    }
    throw new Error(extractAxiosMessage(error, "Failed to load activity log."));
  }
}

/**
 * Get user activity list
 */
export async function listActivities(limit?: number): Promise<ActivityRecord[]> {
  const params = limit ? { limit } : undefined;
  return fetchActivityFeed(params);
}

/**
 * Get recent user activities
 */
export async function listRecentActivities(limit = 5): Promise<ActivityRecord[]> {
  return fetchActivityFeed({ limit });
}

/**
 * Get user profile statistics
 */
export async function profileStats(): Promise<ProfileStats> {
  try {
    const response = await api.get("/api/docs/profile-stats/");
    const data = response.data as Record<string, unknown> | undefined;

    return {
      posts_count: Number(data?.posts_count ?? 0) || 0,
      docs_count: Number(data?.docs_count ?? 0) || 0,
      comments_count: Number(data?.comments_count ?? 0) || 0,
      reactions_count: Number(data?.reactions_count ?? 0) || 0,
      reports_count: Number(data?.reports_count ?? 0) || 0,
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return DEFAULT_PROFILE_STATS;
    }
    throw new Error(extractAxiosMessage(error, "Failed to load profile statistics."));
  }
}

/**
 * Get full user profile data
 */
export async function getUserData(): Promise<UserProfile> {
  try {
    const [userResponse, accountResponse, verification] = await Promise.all([
      api.get("/api/users/me/"),
      api.get("/api/account/onboarding/").catch((error) => {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return { data: null } as { data: null };
        }
        throw error;
      }),
      isUserVerified().catch(() => null),
    ]);

    const userData = (userResponse.data ?? {}) as Record<string, unknown>;
    const accountRaw = accountResponse?.data;
    const accountData =
      accountRaw && typeof accountRaw === "object"
        ? (accountRaw as Record<string, unknown>)
        : {};

    const username = coerceString(userData["username"], "");
    const accountFullName = coerceString(accountData["full_name"], "");
    const userFullName = coerceString(userData["full_name"], "");
    const firstName = coerceString(userData["first_name"], "").trim();
    const lastName = coerceString(userData["last_name"], "").trim();
    const nameFromParts = `${firstName} ${lastName}`.trim();
    const fullName = (userFullName || accountFullName || nameFromParts || username).trim();

    const createdAt =
      coerceString(accountData["created_at"], "") ||
      coerceString(userData["created_at"], "") ||
      coerceString(userData["date_joined"], "") ||
      new Date().toISOString();

    const email = coerceString(userData["email"], "");
    const bioValue = coerceOptionalString(accountData["bio"]);
    const locationValue = coerceOptionalString(accountData["location"]);
    const profilePicture = coerceString(userData["profilePicture"] as string, "") || coerceString(accountData["profile_picture"] as string, "");
    const onboardingCompleted = Boolean(accountData["onboarding_completed"]);

    const verificationPayload =
      (verification as { verified?: boolean; is_verified?: boolean } | null) ?? null;

    const isVerified = Boolean(
      verificationPayload?.verified ?? verificationPayload?.is_verified
    );

    return {
      id: userData["id"] ? String(userData["id"]) : null,
      username,
      email,
      full_name: fullName,
      bio: bioValue ?? "",
      location: locationValue ?? "",
      created_at: createdAt,
      profilePicture,
      is_email_verified: isVerified,
      onboarding_completed: onboardingCompleted,
    };
  } catch (error: unknown) {
    throw new Error(extractAxiosMessage(error, "Failed to fetch user details."));
  }
}

export async function getCurrentUserPicture(): Promise<string | null> {
  try {
    const res = await api.get("/api/users/me/");
    const pic = (res.data?.profile_picture ?? res.data?.profilePicture) as string | null;
    if (!pic) return null;
    return pic.startsWith("http") ? pic : `${API_BASE_URL}${pic}`;
  } catch {
    return null;
  }
}
